<template>
  <div class="model-page">
    <!-- Header Section -->
    <AppHeader 
      header-text="Drivers in your state can save up to $600/year"
      button-text="FREE CALL"
    />

    <!-- Progress Bar -->
    <div class="progress-container">
      <div class="progress-bar">
        <div class="progress-fill"></div>
      </div>
    </div>

    <!-- Navigation -->
    <div class="navigation">
      <div class="container">
        <button class="back-btn" @click="goBack">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Previous Question
        </button>
      </div>
    </div>

    <!-- Model Selection Section -->
    <section class="model-selection">
      <div class="container">
        <div class="model-content">
          <PageTitle title="Vehicle Model" />
          
          <div class="models-grid">
            <button 
              v-for="model in availableModels" 
              :key="model"
              :class="['model-button', { 'selected': selectedModel === model }]"
              @click="selectModel(model)"
            >
              {{ model }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer Links -->
    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import PageTitle from '@/components/PageTitle.vue'
import { submitDataAndLog } from '@/utils/localStorage'
import carInfoData from '@/data/car_info.json'

export default {
  name: 'ModelPage',
  components: {
    AppHeader,
    AppFooter,
    PageTitle
  },
  data() {
    return {
      selectedModel: null,
      userQuoteData: null,
      availableModels: [],
      carInfo: carInfoData,
      selectedYear: null,
      selectedMake: null
    }
  },
  mounted() {
    this.loadUserData()
    this.loadAvailableModels()
  },
  methods: {
    loadUserData() {
      const storedData = localStorage.getItem('quoteData')
      if (storedData) {
        try {
          this.userQuoteData = JSON.parse(storedData)
          
          // 检查是否是添加车辆模式
          if (this.userQuoteData.adding_vehicle && this.userQuoteData.temp_vehicle) {
            this.selectedYear = this.userQuoteData.temp_vehicle.car_year
            this.selectedMake = this.userQuoteData.temp_vehicle.car_make
          } else {
            this.selectedYear = this.userQuoteData.car_year
            this.selectedMake = this.userQuoteData.car_make
          }
        } catch (error) {
          console.error('Error parsing stored data:', error)
          this.$router.push('/')
        }
      } else {
        // 如果没有数据，跳转回首页
        this.$router.push('/')
      }
    },
    
    saveUserData() {
      localStorage.setItem('quoteData', JSON.stringify(this.userQuoteData))
    },
    
    loadAvailableModels() {
      if (!this.selectedYear || !this.selectedMake) {
        console.error('Missing year or make data')
        this.$router.push('/')
        return
      }

      if (!this.carInfo[this.selectedYear] || !this.carInfo[this.selectedYear][this.selectedMake]) {
        console.error('No data available for selected year and make:', this.selectedYear, this.selectedMake)
        return
      }
      
      // 获取选定年份和品牌下的所有车型
      const makeData = this.carInfo[this.selectedYear][this.selectedMake]
      this.availableModels = Object.keys(makeData).sort()
      
      console.log('Available models for', this.selectedYear, this.selectedMake, ':', this.availableModels)
    },
    
    async selectModel(model) {
      this.selectedModel = model
      
      // 检查是否是添加车辆模式
      if (this.userQuoteData.adding_vehicle && this.userQuoteData.temp_vehicle) {
        // 添加车辆模式：更新临时车辆数据
        this.userQuoteData.temp_vehicle.car_model = model
        console.log('Selected model for additional vehicle:', model)
      } else {
        // 第一辆车模式：更新主车辆数据
        this.userQuoteData.car_model = model
        console.log('Selected model for primary vehicle:', model)
      }
      
      this.saveUserData()
      
      // 提交数据并记录页面提交事件
      await submitDataAndLog('model')
      
      // 跳转到车辆拥有权选择页面
      this.$router.push('/own')
    },
    
    goBack() {
      this.$router.push('/make')
    }
  }
}
</script>

<style scoped>
/* Page Layout */
.model-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}



/* Navigation */
.navigation {
  padding: 1rem 0;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #999;
  font-size: 0.9rem;
  cursor: pointer;
  transition: color 0.3s ease;
}

.back-btn:hover {
  color: #666;
}

/* Model Selection */
.model-selection {
  padding: 2rem 0 4rem;
  background: #fff;
  flex: 1;
}

.model-content {
  text-align: center;
  max-width: 1000px;
  margin: 0 auto;
}

.models-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
}

.model-button {
  padding: 1.2rem 1rem;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  color: #007bff;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  min-height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.model-button:hover {
  border-color: #007bff;
  background: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,123,255,0.15);
}

.model-button.selected {
  border-color: #007bff;
  background: #007bff;
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,123,255,0.3);
}

/* Responsive Design */
@media (max-width: 768px) {
  .models-grid {
    grid-template-columns: 1fr;
    gap: 0.8rem;
    padding: 0 1rem;
  }

  .model-button {
    padding: 1rem;
    font-size: 0.9rem;
    min-height: 55px;
    text-align: left;
    justify-content: flex-start;
  }

  .model-selection {
    padding: 1.5rem 0 2rem;
  }
}

@media (max-width: 480px) {
  .models-grid {
    gap: 0.6rem;
  }

  .model-button {
    padding: 0.9rem;
    font-size: 0.85rem;
    min-height: 50px;
  }

  .model-selection {
    padding: 1rem 0;
  }
}
</style>
